// Registro dos artigos de ajuda (ADR 0027). O texto de cada artigo vive em
// content/<idioma>/<slug>.md e vai junto com o build, um pacote por idioma,
// carregado só quando a ajuda abre. Títulos e resumos ficam em help.i18n.ts,
// para a lista aparecer sem baixar o conteúdo e para o compilador cobrar os
// três idiomas.
import type { Locale } from "../i18n/core";

export type HelpLevel = "basic" | "intermediate" | "advanced";

export type HelpArticle = {
  slug: string;
  level: HelpLevel;
  /** Tela da console que o artigo explica; vira o botão "Abrir a tela". */
  route?: string;
  /** Artigos ligados, na ordem de leitura sugerida. */
  related: string[];
};

export const HELP_LEVELS: HelpLevel[] = ["basic", "intermediate", "advanced"];

export const ARTICLES: HelpArticle[] = [
  { slug: "first-steps", level: "basic", route: "/overview", related: ["install-agent", "policies", "restore"] },
  { slug: "trial", level: "basic", related: ["first-steps", "install-agent", "storage"] },
  { slug: "install-agent", level: "basic", route: "/add-machines", related: ["machines", "policies", "troubleshooting"] },
  { slug: "machines", level: "basic", route: "/machines", related: ["activity-alerts", "restore", "troubleshooting"] },
  { slug: "policies", level: "basic", route: "/policies", related: ["storage", "image-backup", "databases"] },
  { slug: "restore", level: "basic", route: "/machines", related: ["machines", "image-backup", "databases"] },
  { slug: "activity-alerts", level: "basic", route: "/activity", related: ["machines", "users-access", "troubleshooting"] },
  { slug: "storage", level: "intermediate", route: "/storage", related: ["policies", "security", "organizations"] },
  { slug: "image-backup", level: "intermediate", route: "/policies", related: ["policies", "restore"] },
  { slug: "databases", level: "intermediate", route: "/machines", related: ["policies", "restore", "troubleshooting"] },
  { slug: "shares", level: "intermediate", route: "/machines", related: ["users-access", "restore", "troubleshooting"] },
  { slug: "reports", level: "intermediate", route: "/reports", related: ["activity-alerts", "security", "users-access"] },
  { slug: "users-access", level: "intermediate", route: "/settings", related: ["security", "api", "organizations"] },
  { slug: "organizations", level: "advanced", route: "/organizations", related: ["users-access", "storage", "platform-storage", "trial"] },
  { slug: "security", level: "advanced", related: ["storage", "reports", "users-access"] },
  { slug: "troubleshooting", level: "advanced", related: ["install-agent", "machines", "databases"] },
  { slug: "platform-storage", level: "advanced", route: "/platform-storage", related: ["storage", "organizations", "trial"] },
  { slug: "api", level: "advanced", route: "/settings", related: ["users-access", "reports"] },
];

export const ARTICLE_BY_SLUG: Record<string, HelpArticle> = Object.fromEntries(ARTICLES.map((a) => [a.slug, a]));

/** Tela (terceiro segmento da rota) → artigo que a explica; o "?" da barra usa isto. */
const BY_SECTION: Record<string, string> = {
  overview: "first-steps",
  machines: "machines",
  "add-machines": "install-agent",
  policies: "policies",
  storage: "storage",
  usage: "storage",
  activity: "activity-alerts",
  reports: "reports",
  organizations: "organizations",
  settings: "users-access",
  "platform-storage": "platform-storage",
  help: "first-steps",
};

/** Artigo para a rota atual: `/o/<org>/machines/<id>/restore` cai em "restore". */
export function articleForPath(pathname: string): string {
  const parts = pathname.split("/").filter(Boolean);
  const section = parts[2] ?? "";
  if (section === "machines" && parts[4] === "restore") return "restore";
  if (section === "machines" && parts[4] === "databases") return "databases";
  return BY_SECTION[section] ?? "first-steps";
}

const bundles = import.meta.glob<Record<string, string>>("./content/*/index.ts", { import: "articles" });

/** Conteúdo dos artigos no idioma, por slug. */
export async function loadHelp(locale: Locale): Promise<Record<string, string>> {
  const load = bundles[`./content/${locale}/index.ts`];
  if (!load) return {};
  return load();
}
